import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export type ShiftActionScope = 'one' | 'all';

interface Props {
    /** Names the radio group, for the delete or the edit window */
    name: string;
    value: ShiftActionScope;
    onChange: (scope: ShiftActionScope) => void;
    passengerName: string;
    allCount: number;
}

const Options = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 4px 16px 0;
    box-sizing: border-box;
    direction: rtl;
`;

const Option = styled.label<{ $checked: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 10px;
    border: 1px solid ${({ $checked }) => ($checked ? '#1976d2' : '#c4c4c4')};
    border-radius: 8px;
    background: ${({ $checked }) => ($checked ? '#f2f8fe' : 'white')};
    font-size: 14px;
    cursor: pointer;

    input {
        margin: 0;
        width: 16px;
        height: 16px;
        accent-color: #1976d2;
    }
`;

/**
 * "Only this passenger" or "all N selected passengers" for a shift action. Shown only when the clicked
 * passenger is selected and other selected passengers have the same shift (customer request, 2026-11-02):
 * the multi-passenger delete existed, but only after holding the mouse on the trash icon for half a second.
 */
const ScopeChoice: FC<Props> = ({ name, value, onChange, passengerName, allCount }) => {
    const { t } = useTranslation();

    return (
        <Options role="radiogroup" aria-label={name}>
            <Option $checked={value === 'one'}>
                <input
                    type="radio"
                    name={name}
                    checked={value === 'one'}
                    onChange={() => onChange('one')}
                />
                {t('onlyThisPassenger', { name: passengerName })}
            </Option>
            <Option $checked={value === 'all'}>
                <input
                    type="radio"
                    name={name}
                    checked={value === 'all'}
                    onChange={() => onChange('all')}
                />
                {t('allSelectedPassengers', { count: allCount })}
            </Option>
        </Options>
    );
};

export default ScopeChoice;
